import { Signal } from '@preact/signals-react';
import { EditorDeclarativeBlock } from '@/webapi/use-experience-api';
import {
  HistoryCheckpoint,
  SessionListEntry,
} from '@/features/ai-agents/shared/use-ai-metadata-api';
import {
  AiChatMessage,
  SelectedAgent,
  ContextualAttachment,
} from '@/features/ai-agents/shared/models';
import {
  AiComponentHtmlAttribute,
  AiComponentJsVariable,
  AiManifestComponent,
  VideoAttributes,
} from '@/pkg/ai';
import { ParsedCssRule } from '@/features/editor/widgets/shared/ai-editing-layout/manipulators/use-css-manipulator';
import { AiConversationStarter } from '@/features/ai-agents/chat/starters';
import { AiOriginAction } from '@/pkg/sdk';

export type VisualGroupInfo = {
  group: string;
  selector: string;
};

export type AiComponentContextProps = {
  originalChange: EditorDeclarativeBlock;
  selectedAgent?: SelectedAgent;
  originAction: AiOriginAction;
  starters?: AiConversationStarter;
  initialContextualAttachments?: ContextualAttachment[];
};

export enum AiComponentTab {
  PROMPT = `prompt`,
  EDIT = `edit`,
  CODE = `code`,
}

export interface AiComponentState {
  newChange: Signal<EditorDeclarativeBlock>;
  lastSavedChange: Signal<EditorDeclarativeBlock>;
  selectedTab: Signal<AiComponentTab>;
  highlightedVisualGroup?: Signal<VisualGroupInfo | undefined>;
  selectedCodeTab: Signal<`HTML` | `CSS` | `JS`>;
  sessionId: Signal<string>;
  firstSessionId: Signal<string>;
  lastRunId: Signal<string>;
  selectedAgent: Signal<SelectedAgent>;

  backToTab: Signal<AiComponentTab | null>;

  isProcessing: Signal<boolean>;
  isProcessingVisualManifest: Signal<boolean>;

  isFullScreen: Signal<boolean>;
  isComponentReady: Signal<boolean>;
  hasChanges: Signal<boolean>;
  hasVisualEditManifest: Signal<boolean>;
  sessionData: Signal<Record<string, any>>;
  contextualAttachments: Signal<ContextualAttachment[]>;
  starters?: AiConversationStarter;
}

export interface AiComponentActions {
  selectTab: (tab: AiComponentTab) => void;
  onSelectedCodeTab: (tab: `HTML` | `CSS` | `JS`) => void;
  onNewSessionId: (session: SessionListEntry) => void;
  onNewMessageSent: (message: AiChatMessage) => void;
  onPromptReply: (
    agentMessage: AiChatMessage,
    userMessage: AiChatMessage,
    sessionId: string,
  ) => void;
  onSessionLoaded?: (messages: AiChatMessage[]) => void;
  setHtml: (html: string) => void;
  setCss: (css: string) => void;
  setJs: (js: string) => void;
  setEditManifest: (manifest: string) => void;
  toggleFullScreen: () => void;
  applyChanges: () => void;
  discardChanges: () => void;
  saveLocalChanges: () => void;
  discardLocalChanges: () => void;

  getTextContent: (selector: string) => string | undefined;
  setTextContent: (selector: string, value: string, richText?: boolean) => void;
  getHtmlContent: (selector: string) => string | undefined;
  setHtmlContent: (selector: string, value: string) => void;
  getAttribute: (
    selector: string,
    attribute: AiComponentHtmlAttribute,
  ) => string;
  setAttribute: (
    selector: string,
    attribute: AiComponentHtmlAttribute,
    value: string,
  ) => void;
  getVideoAttributes: (selector: string) => VideoAttributes | undefined;
  setVideoAttributes: (selector: string, attrs: VideoAttributes) => void;

  onLoadingChange: (loading: boolean) => void;

  getCssRule: (selector: string) => ParsedCssRule | undefined;
  setCssRule: (input: ParsedCssRule) => void;
  getManifestVariables: () => AiComponentJsVariable[];
  setVariable: (variable: AiComponentJsVariable) => void;
  isFirstInGroup: (component: AiManifestComponent) => boolean;

  restoreCheckpoint: (checkpoint: HistoryCheckpoint) => Promise<void>;

  setBackToTab: (tab: AiComponentTab | null) => void;

  setContextualAttachments: (attachments: ContextualAttachment[]) => void;
  getContextualAttachments: () => ContextualAttachment[];

  showAddedElements: () => void;
}
